import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { appConfig } from '../config/app';

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  snackbar: {
    open: false,
    message: '',
    severity: 'info'
  },
  alert: {
    open: false,
    message: '',
    severity: 'info'
  }
};

// Action types
export const ActionTypes = {
  SET_USER: 'SET_USER',
  SET_LOADING: 'SET_LOADING',
  SHOW_SNACKBAR: 'SHOW_SNACKBAR',
  HIDE_SNACKBAR: 'HIDE_SNACKBAR',
  SHOW_ALERT: 'SHOW_ALERT',
  HIDE_ALERT: 'HIDE_ALERT',
  LOGOUT: 'LOGOUT'
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload
      };
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    case ActionTypes.SHOW_SNACKBAR:
      return {
        ...state,
        snackbar: {
          open: true,
          message: action.payload.message,
          severity: action.payload.severity || 'info'
        }
      };
    case ActionTypes.HIDE_SNACKBAR:
      return {
        ...state,
        snackbar: {
          ...state.snackbar,
          open: false
        }
      };
    case ActionTypes.SHOW_ALERT:
      return {
        ...state,
        alert: {
          open: true,
          message: action.payload.message,
          severity: action.payload.severity || 'info'
        }
      };
    case ActionTypes.HIDE_ALERT:
      return {
        ...state,
        alert: {
          ...state.alert,
          open: false
        }
      };
    case ActionTypes.LOGOUT:
      // Clear localStorage
      localStorage.removeItem(appConfig.storage.token);
      localStorage.removeItem(appConfig.storage.user);
      return {
        ...initialState
      };
    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load initial data from localStorage
  useEffect(() => {
    console.log('=== AppContext Initial Load ===');
    const token = localStorage.getItem(appConfig.storage.token);
    const userData = localStorage.getItem(appConfig.storage.user);

    console.log('Storage keys:', appConfig.storage);
    console.log('Token from localStorage:', token ? 'Present' : 'Missing');
    console.log('User data from localStorage:', userData);

    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        console.log('Parsed user data:', user);
        dispatch({ type: ActionTypes.SET_USER, payload: user });
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem(appConfig.storage.token);
        localStorage.removeItem(appConfig.storage.user);
      }
    } else {
      console.log('No token or user data found in localStorage');
    }
  }, []);

  // Action creators
  const actions = {
    setUser: (user) => {
      if (user) {
        localStorage.setItem(appConfig.storage.user, JSON.stringify(user));
      }
      dispatch({ type: ActionTypes.SET_USER, payload: user });
    },
    
    setLoading: (loading) => {
      dispatch({ type: ActionTypes.SET_LOADING, payload: loading });
    },
    
    showSnackbar: (message, severity = 'info') => {
      dispatch({ 
        type: ActionTypes.SHOW_SNACKBAR, 
        payload: { message, severity } 
      });
    },
    
    hideSnackbar: () => {
      dispatch({ type: ActionTypes.HIDE_SNACKBAR });
    },
    
    showAlert: (message, severity = 'info') => {
      dispatch({ 
        type: ActionTypes.SHOW_ALERT, 
        payload: { message, severity } 
      });
    },
    
    hideAlert: () => {
      dispatch({ type: ActionTypes.HIDE_ALERT });
    },
    
    logout: () => {
      dispatch({ type: ActionTypes.LOGOUT });
    }
  };

  return (
    <AppContext.Provider value={{ state, actions }}>
      {children}
    </AppContext.Provider>
  );
};

// Hook to use context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}; 